import OneTrustPlaceholder from '../OneTrustPlaceholder';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import {
  OneTrustDefaultConsentPreference,
  OneTrustGroup,
  useOneTrustConsent,
} from 'lib/hooks/useOneTrustConsent';

// Re-exported for backward compatibility with existing consumers (e.g. Shopping.tsx).
export { OneTrustDefaultConsentPreference };

type DefaultConsentPreference = `${OneTrustDefaultConsentPreference}`;

interface IOneTrustFilter {
  filteredComponent: React.ReactNode; // The component OneTrust applies to
  defaultConsentPref?: DefaultConsentPreference;
  optanonGroups: OneTrustGroup[];
  enablePlaceholder: boolean;
  placeholderFeatureType?:
    | 'video'
    | 'buynow'
    | 'social'
    | 'ratings'
    | 'productlocator'
    | 'retailerlist'; // Determines consent message to display
}

function resolveDefaultConsentPreference(
  siteSettingValue?: string,
  fallbackValue: DefaultConsentPreference = OneTrustDefaultConsentPreference.Consent
): DefaultConsentPreference {
  return Object.values(OneTrustDefaultConsentPreference).includes(
    siteSettingValue as OneTrustDefaultConsentPreference
  )
    ? (siteSettingValue as DefaultConsentPreference)
    : fallbackValue;
}

export default function OneTrustFilter({
  filteredComponent,
  defaultConsentPref,
  optanonGroups,
  enablePlaceholder,
  placeholderFeatureType,
}: IOneTrustFilter) {
  const siteSettings = useSiteSettings();
  const siteSettingDefaultConsentPref = siteSettings?.oneTrustDefaultConsentPreference?.value;
  const resolvedDefaultConsentPref = resolveDefaultConsentPreference(
    siteSettingDefaultConsentPref,
    defaultConsentPref
  );
  const { isBlocked, readyToMount, oneTrustEnabled } = useOneTrustConsent(
    optanonGroups,
    resolvedDefaultConsentPref
  );

  if (!oneTrustEnabled) {
    return <></>;
  }

  return (
    <>
      {isBlocked && enablePlaceholder && (
        <OneTrustPlaceholder featureType={placeholderFeatureType} />
      )}
      {isBlocked && !enablePlaceholder && <></>}
      {!isBlocked && readyToMount && filteredComponent}
    </>
  );
}
